import React, { Fragment, useState, useEffect, useMemo } from "react";
import {
  Card,
  Select,
  Button,
  Dropdown,
  Menu,
  Affix,
  Popconfirm,
  Typography,
  Breadcrumb,
  Input,
  Space,
} from "antd";
import ListTable from "common/ListTable";
import Header from "components/Common/Header";
import { isMobileView } from "components/Common/AppHelper";
import { Link, useLocation } from "react-router-dom";
import { MoreOutlined, EditOutlined, DeleteTwoTone, EyeOutlined } from "@ant-design/icons";
import {
  NOTIFICATION_SETTINGS
} from "apiServices/API_ENDPOINTS";
import { getData, deleteData, postData } from "apiServices/common";
import CustomFilter from "components/Common/CustomFilter";
import { alertPop } from "apiServices/common/helper";
import { commaSeparateNumber, hasPermission } from "utils/functions";
import { NO_PERMISSION_REQUIRED } from "routes/permissions";

const NotificationSettings = (props) => {
  // Props
  const { width } = props;

  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  //   States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [costList, setCostList] = useState(null);
  const [settingList, setSettingList] = useState(null);
  const [totalData, setTotalData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState();
  const [fetchError, setFetchError] = useState(null);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      render: (_,record) => <span>{(record?.slug_details?.code)}</span>,
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  const getAllSettings = async (filterValues) => {
    setLoading(true);
    setFetchError(null);

    const bodyData = filterValues;
    const response = await getData(NOTIFICATION_SETTINGS, false, bodyData);

    if (response && response?.data?.code === 200) {
      setSettingList(response?.data?.data?.data);
      setTotalData(response?.data?.data?.total);
      setRangeData(response?.data?.data?.per_page);
    } else {
      setFetchError("Could not load this list.");
      alertPop(
        "error",
        response?.messages ? response?.messages : "Something Went Wrong!",
      );
    }

    setLoading(false);
  };

  useEffect(() => {
    // Get all cost data on fist load
    // getAllCost();
    setLoading(true);
    getAllSettings({ page: currentPage });
  }, [currentPage]);

  const removeNotificationSettings = async (value) => {
    const response = await deleteData(`${NOTIFICATION_SETTINGS}/${value}`);
    if (response && response?.code === 200) {
      alertPop("success", "Deleted Successfully!");
      // getAllCost();
      getAllSettings();
    } else {
      alertPop(
        "error",
        response?.data?.message
          ? response?.data?.message[0]
          : "Something Went Wrong!",
      );
    }
  };

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <Link to={`/settings/notification-settings-update/${data.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          key: "0",
          disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
        },
        {
          label: (
            <Link to={`/settings/notification-settings-view/${data.id}`}>
              <EyeOutlined /> View
            </Link>
          ),
          key: "1",
          disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
        },
        {
          label: (
            <Popconfirm
              title="Are you sure you want to delete?"
              onConfirm={() => removeNotificationSettings(data.id)}
            >
              <DeleteTwoTone /> Delete
            </Popconfirm>
          ),
          key: "2",
          disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
        },
      ]}
    />
  );

  const paginate = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const extra = (
    <Space>
      <Button
        type={showFilter ? "danger" : "primary"}
        size="small"
        onClick={() => setShowFilter((prev) => !prev)}
        style={{ float: "right" }}
      >
        {`${showFilter ? "Close" : "Open"} Filter Option`}
      </Button>
      <Button key="3" size="small" type="primary">
        <Link to="/settings/notification-settings/create">Add New</Link>
      </Button>
    </Space>
  );

  const memoizedCustomFilter = useMemo(
      () => (
        <CustomFilter
            showFilter={showFilter}
            setShowFilter={setShowFilter}
            tableData={settingList}
            fetchTableData={getAllSettings}
            filterName="notification_settings"
            // filterName="cost_estimate"
          />
      ),
      [showFilter],
    );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}
        >
          <Header
            title={<Title level={5}>Notification Settings</Title>}
            actions={extra}
          />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          {memoizedCustomFilter}
          <ListTable
            loading={loading}
            error={fetchError}
            onRetry={() => getAllSettings({ page: currentPage })}
            emptyText="No records to show yet."
            dataSource={settingList}
            columns={columns}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
};

export default NotificationSettings;
